import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cornerstone from "cornerstone-core";
import dicomParser from "dicom-parser";
import { api } from "../api";
import { ClinicalDocEditor } from "./ClinicalDocEditor";
import { Move, Contrast, ZoomIn, ZoomOut, Maximize2, RotateCw, Ruler, LayoutGrid, RefreshCw, FileText, Sparkles, Trash2, X } from "lucide-react";

// Viewer DICOM: usa cornerstone-wado-image-loader (dynamic-import servito da /public/cornerstone) per decodificare anche
// le transfer syntax COMPRESSE (JPEG Lossless, JPEG-LS, JPEG 2000 ...) tramite i codec WASM ufficiali. Nessun decoder
// improvvisato. I byte arrivano dall'endpoint backend protetto (bearer, owner-only). Se il loader non è disponibile,
// fallback al renderer nativo per DICOM NON compressi monocromatici 8/16-bit (nessuna falsa compatibilità).
const BUF = {};          // ingestId:objectId -> Uint8Array (byte grezzi, per metadati e fallback)
const IMGID = {};        // ingestId:objectId -> imageId del loader wado
let _wadoP = null;

function loadWado() {
  if (_wadoP) return _wadoP;
  _wadoP = new Promise((resolve, reject) => {
    if (window.cornerstoneWADOImageLoader) return resolve(window.cornerstoneWADOImageLoader);
    const s = document.createElement("script");
    s.src = "/cornerstone/cornerstoneWADOImageLoader.min.js";  // r.p => /cornerstone/ : worker+wasm si risolvono qui
    s.async = true;
    s.onload = () => {
      const w = window.cornerstoneWADOImageLoader;
      if (!w) return reject(new Error("loader_missing"));
      try {
        w.external.cornerstone = cornerstone;
        w.external.dicomParser = dicomParser;
        w.webWorkerManager.initialize({
          maxWebWorkers: Math.max(1, Math.min(2, (navigator.hardwareConcurrency || 1))),
          startWebWorkersOnDemand: true,
          taskConfiguration: { decodeTask: { initializeCodecsOnStartup: false, strict: false } },
        });
      } catch { /* già inizializzato */ }
      resolve(w);
    };
    s.onerror = () => reject(new Error("loader_load_failed"));
    document.body.appendChild(s);
  });
  return _wadoP;
}

// Fallback nativo: DICOM NON compresso monocromatico (Explicit/Implicit VR LE 8/16-bit).
function makeNativeImage(imageId, byteArray) {
  if (!byteArray || byteArray.length < 132 || byteArray[128] !== 68 || byteArray[129] !== 73 || byteArray[130] !== 67 || byteArray[131] !== 77) { const e = new Error("nopixels"); e.code = "nopixels"; throw e; }
  const ds = dicomParser.parseDicom(byteArray);
  const ts = ds.string("x00020010") || "1.2.840.10008.1.2";
  if (ts.startsWith("1.2.840.10008.1.2.4") || ts.startsWith("1.2.840.10008.1.2.5")) { const e = new Error("unsupported"); e.code = "unsupported"; throw e; }
  const pd = ds.elements.x7fe00010;
  if (!pd) { const e = new Error("nopixels"); e.code = "nopixels"; throw e; }
  const samples = ds.uint16("x00280002") || 1;
  if (samples > 1) { const e = new Error("unsupported"); e.code = "unsupported"; throw e; }
  const rows = ds.uint16("x00280010"), cols = ds.uint16("x00280011");
  const bits = ds.uint16("x00280100") || 16, rep = ds.uint16("x00280103") || 0;
  const off = pd.dataOffset, len = pd.length;
  let pixels;
  if (bits <= 8) pixels = new Uint8Array(byteArray.buffer, byteArray.byteOffset + off, len);
  else pixels = rep ? new Int16Array(byteArray.buffer, byteArray.byteOffset + off, len / 2)
                    : new Uint16Array(byteArray.buffer, byteArray.byteOffset + off, len / 2);
  let min = pixels[0], max = pixels[0];
  for (let i = 1; i < pixels.length; i++) { const v = pixels[i]; if (v < min) min = v; if (v > max) max = v; }
  const f = (tg) => parseFloat((ds.string(tg) || "").split("\\")[0]);
  const wc = f("x00281050") || (min + max) / 2, ww = f("x00281051") || (max - min) || 1;
  const ps = (ds.string("x00280030") || "").split("\\");
  const photo = ds.string("x00280004") || "MONOCHROME2";
  return { imageId, color: false, rows, columns: cols, height: rows, width: cols,
    minPixelValue: min, maxPixelValue: max, slope: f("x00281053") || 1, intercept: f("x00281052") || 0,
    windowCenter: wc, windowWidth: ww, invert: photo === "MONOCHROME1",
    rowPixelSpacing: parseFloat(ps[0]) || undefined, columnPixelSpacing: parseFloat(ps[1]) || undefined,
    getPixelData: () => pixels, sizeInBytes: len };
}
try { cornerstone.registerImageLoader("pamcnative", (imageId) => ({ promise: new Promise((res, rej) => { const key = imageId.slice("pamcnative:".length); try { res(makeNativeImage(imageId, BUF[key])); } catch (e) { rej(e); } }) })); } catch { /* già registrato */ }

// --- Immagini raster (JPG/JPEG/PNG, colore e scala di grigi) via loader web di cornerstone-core ---
// Nessuna conversione in DICOM, nessun metadato/misura DICOM falsi: pixel spacing assente -> misura in px.
const RASTER_IMG = {};
const RASTERID = {};
function buildWebImage(imageId, url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0);
      let rgba = null;
      const getPixelData = () => { if (!rgba) rgba = ctx.getImageData(0, 0, canvas.width, canvas.height).data; return rgba; };
      resolve({ imageId, minPixelValue: 0, maxPixelValue: 255, slope: 1, intercept: 0,
        windowCenter: 128, windowWidth: 256, render: cornerstone.renderWebImage,
        getCanvas: () => canvas, getImage: () => image, getPixelData,
        rows: image.naturalHeight, columns: image.naturalWidth, height: image.naturalHeight, width: image.naturalWidth,
        color: true, rgba: true, invert: false, columnPixelSpacing: undefined, rowPixelSpacing: undefined,
        sizeInBytes: image.naturalWidth * image.naturalHeight * 4 });
    };
    image.onerror = () => reject(new Error("raster_load_failed"));
    image.src = url;
  });
}
try { cornerstone.registerImageLoader("pamcraster", (imageId) => { const k = imageId.slice("pamcraster:".length); return { promise: RASTER_IMG[k] ? Promise.resolve(RASTER_IMG[k]) : Promise.reject(new Error("raster_missing")) }; }); } catch { /* già registrato */ }

async function resolveRaster(inst) {
  const k = inst.object_id;
  if (RASTERID[k]) return RASTERID[k];
  const blob = await inst.fetchBlob();
  RASTER_IMG[k] = await buildWebImage(`pamcraster:${k}`, URL.createObjectURL(blob));
  RASTERID[k] = `pamcraster:${k}`;
  return RASTERID[k];
}
// DICOM fornito come Blob (upload YouPAM o endpoint /exams legacy): stessi codec WADO del percorso ingest.
async function resolveDicomBlob(inst) {
  const key = `dblob:${inst.object_id}`;
  if (IMGID[key]) return IMGID[key];
  const blob = await inst.fetchBlob();
  const bytes = new Uint8Array(await blob.arrayBuffer());
  BUF[key] = bytes;
  try { const w = await loadWado(); IMGID[key] = w.wadouri.fileManager.add(new Blob([bytes], { type: "application/dicom" })); }
  catch { IMGID[key] = `pamcnative:${key}`; }
  return IMGID[key];
}
async function resolveAny(inst) {
  if (inst.kind === "raster") return resolveRaster(inst);
  if (inst.ingestId) return resolveImageId(inst.ingestId, inst.object_id);
  return resolveDicomBlob(inst);
}

// Costruisce la lista unificata di immagini di un fascicolo (studio DICOM ingest + upload raster/DICOM + DICOM legacy).
export async function resolveFolderItems(f) {
  const items = [];
  let dicomFromIngest = false;
  if (f.pam_ingest_id) {
    try {
      const { data } = await api.get(`/pam-connect/studies/${f.pam_ingest_id}`);
      (data?.series || []).forEach((s) => s.instances.forEach((i) =>
        items.push({ object_id: i.object_id, series_uid: s.series_uid, kind: "dicom", ingestId: f.pam_ingest_id })));
      if (items.length) dicomFromIngest = true;
    } catch { /* fallback /exams sotto */ }
  }
  (f.images || []).forEach((im) => {
    if (im.source_system === "youpam_upload") {
      const raster = im.kind !== "dicom";
      items.push({ object_id: im.image_id, series_uid: raster ? "upload" : "dicom", kind: raster ? "raster" : "dicom-blob",
        mime: im.mime, canDelete: !!im.can_delete,
        fetchBlob: () => api.get(`/exams/${f.id}/images/${im.image_id}/file`, { responseType: "blob" }).then((r) => new Blob([r.data], { type: im.mime })) });
      return;
    }
    if (dicomFromIngest) return; // immagini DICOM già fornite dalla serie ingest (clinico titolare dell'archivio)
    if (im.source_system === "pam_connect" && im.object_id) {
      // Paziente (o share attiva): stream diretto dell'oggetto DICOM esistente, nessuna duplicazione, gate referto lato server.
      items.push({ object_id: im.object_id, series_uid: "dicom", kind: "dicom-blob",
        fetchBlob: () => api.get(`/exams/${f.id}/dicom-objects/${im.object_id}/file`, { responseType: "blob" }).then((r) => new Blob([r.data], { type: "application/dicom" })) });
    }
  });
  return items;
}

async function fetchBytes(ingestId, oid) {
  const key = `${ingestId}:${oid}`;
  if (!BUF[key]) { const res = await api.get(`/pam-connect/studies/${ingestId}/instances/${oid}/file`, { responseType: "arraybuffer" }); BUF[key] = new Uint8Array(res.data); }
  return BUF[key];
}

async function resolveImageId(ingestId, oid) {
  const key = `${ingestId}:${oid}`;
  if (IMGID[key]) return IMGID[key];
  const bytes = await fetchBytes(ingestId, oid);
  try {
    const w = await loadWado();
    const id = w.wadouri.fileManager.add(new Blob([bytes], { type: "application/dicom" }));
    IMGID[key] = id;
    return id;
  } catch {
    IMGID[key] = `pamcnative:${key}`;  // loader non disponibile -> fallback nativo (solo non compresso)
    return IMGID[key];
  }
}

// Singola cella viewport cornerstone (immagine + interazioni). La misura è attiva solo nella cella "focus" in layout 1x1.
function Cell({ ingestId, instance, focused, onFocus, measure, setMeasureInfo, mode }) {
  const { t } = useTranslation();
  const elRef = useRef(null);
  const [msg, setMsg] = useState(t("diag.vw_loading"));
  const pts = useRef([]);

  const redrawMeasure = useCallback(() => {
    const el = elRef.current; if (!el) return;
    let ov = el.querySelector("canvas.pamc-measure");
    const cv = el.querySelector("canvas.cornerstone-canvas") || el.querySelector("canvas");
    if (!cv) return;
    if (!ov) { ov = document.createElement("canvas"); ov.className = "pamc-measure"; ov.style.cssText = "position:absolute;left:0;top:0;pointer-events:none;z-index:5"; el.appendChild(ov); }
    ov.width = cv.width; ov.height = cv.height; ov.style.width = cv.style.width; ov.style.height = cv.style.height;
    const ctx = ov.getContext("2d"); ctx.clearRect(0, 0, ov.width, ov.height);
    if (pts.current.length === 0) return;
    const img = cornerstone.getImage(el);
    const toCanvas = (p) => cornerstone.pixelToCanvas(el, p);
    ctx.strokeStyle = "#38bdf8"; ctx.fillStyle = "#38bdf8"; ctx.lineWidth = 2;
    pts.current.forEach((p) => { const c = toCanvas(p); ctx.beginPath(); ctx.arc(c.x, c.y, 4, 0, 2 * Math.PI); ctx.fill(); });
    if (pts.current.length === 2) {
      const a = toCanvas(pts.current[0]), b = toCanvas(pts.current[1]);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      const dx = (pts.current[1].x - pts.current[0].x), dy = (pts.current[1].y - pts.current[0].y);
      const rs = img?.rowPixelSpacing, cs = img?.columnPixelSpacing;
      const label = (rs && cs) ? `${Math.sqrt((dx * cs) ** 2 + (dy * rs) ** 2).toFixed(1)} mm` : `${Math.sqrt(dx * dx + dy * dy).toFixed(0)} px`;
      ctx.font = "13px sans-serif"; ctx.fillStyle = "#e0f2fe"; ctx.fillText(label, (a.x + b.x) / 2 + 6, (a.y + b.y) / 2 - 6);
      setMeasureInfo && setMeasureInfo(label);
    }
  }, [setMeasureInfo]);

  const show = useCallback(async () => {
    const el = elRef.current; if (!el || !instance) return;
    setMsg(t("diag.vw_loading")); pts.current = [];
    try {
      const imageId = await resolveAny(instance);
      try { cornerstone.getEnabledElement(el); } catch { cornerstone.enable(el); }
      const img = await cornerstone.loadImage(imageId);
      cornerstone.displayImage(el, img); cornerstone.reset(el); setMsg(null); redrawMeasure();
    } catch (e) {
      const code = e?.code || (e?.error?.message || e?.message || "");
      setMsg(/nopixels/.test(code) ? t("diag.vw_nopixels") : /unsupported/.test(code) ? t("diag.vw_unsupported") : t("diag.vw_decode_err"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestId, instance, redrawMeasure]);

  useEffect(() => { show(); }, [show]);
  useEffect(() => {
    const el = elRef.current; if (!el) return;
    const h = () => redrawMeasure(); el.addEventListener("cornerstoneimagerendered", h);
    return () => { el.removeEventListener("cornerstoneimagerendered", h); try { cornerstone.disable(el); } catch { /* noop */ } };
  }, [redrawMeasure]);

  const vp = () => { try { return cornerstone.getViewport(elRef.current); } catch { return null; } };
  const apply = (fn) => { const v = vp(); if (!v) return; fn(v); cornerstone.setViewport(elRef.current, v); };
  const onWheel = (e) => { e.preventDefault(); apply((v) => { v.scale = Math.max(0.2, v.scale * (e.deltaY < 0 ? 1.15 : 0.87)); }); };
  const onDown = (e) => {
    onFocus && onFocus();
    if (measure && focused) {
      const p = cornerstone.pageToPixel(elRef.current, e.pageX, e.pageY);
      if (pts.current.length >= 2) pts.current = [];
      pts.current.push(p); redrawMeasure(); return;
    }
    const sx = e.clientX, sy = e.clientY, v0 = vp(); if (!v0) return;
    const wl = mode === "wl" || e.button === 2 || e.shiftKey; const t0 = { ...v0.translation }, voi0 = { ...v0.voi };
    const move = (m) => apply((v) => {
      if (wl) { v.voi.windowWidth = Math.max(1, voi0.windowWidth + (m.clientX - sx) * 2); v.voi.windowCenter = voi0.windowCenter + (m.clientY - sy) * 2; }
      else { v.translation.x = t0.x + (m.clientX - sx) / v.scale; v.translation.y = t0.y + (m.clientY - sy) / v.scale; }
    });
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  // handle azioni esposte via ref dataset sull'elemento DOM (lette dalla toolbar)
  useEffect(() => { if (elRef.current) elRef.current._apply = apply; }); // eslint-disable-line

  return (<div className={`relative bg-black ${focused ? "ring-2 ring-sky-500" : ""}`} data-testid="dv-cell">
    <div ref={elRef} onWheel={onWheel} onMouseDown={onDown} onContextMenu={(e) => e.preventDefault()}
      className="absolute inset-0" style={{ width: "100%", height: "100%" }} data-testid="dv-canvas" />
    {msg && <div className="absolute inset-0 flex items-center justify-center text-center text-slate-300 text-xs p-4 pointer-events-none" data-testid="dv-message">{msg}</div>}
  </div>);
}

export function DicomViewer({ ingestId, series, items, onClose, folderId, onSaved, onDeleteImage }) {
  const { t } = useTranslation();
  const [editor, setEditor] = useState(false);
  const instances = useMemo(() => (items && items.length)
    ? items
    : (series || []).flatMap((s) => s.instances.map((i) => ({ ...i, series_uid: s.series_uid, kind: "dicom", ingestId }))), [items, series, ingestId]);
  const [idx, setIdx] = useState(0);
  const [layout, setLayout] = useState(1);          // 1 = 1x1, 4 = 2x2
  const [measure, setMeasure] = useState(false);
  const [measureInfo, setMeasureInfo] = useState(null);
  const [focus, setFocus] = useState(0);
  const [mode, setMode] = useState("pan");          // pan | wl (contrasto)

  const cells = useMemo(() => {
    const n = layout === 4 ? 4 : 1;
    return Array.from({ length: n }, (_, k) => instances[idx + k]).filter(Boolean);
  }, [instances, idx, layout]);

  // Pulisce il risultato misura al cambio immagine/layout o alla disattivazione (il badge flottante non resta stantio)
  useEffect(() => { setMeasureInfo(null); }, [idx, layout]);
  useEffect(() => { if (layout === 4) setMeasure(false); }, [layout]);
  useEffect(() => { if (!measure) setMeasureInfo(null); }, [measure]);
  const current = instances[idx];
  const handleDelete = async () => {
    if (!onDeleteImage || !current?.canDelete) return;
    if (!window.confirm(t("diag.vw_delete_confirm"))) return;
    await onDeleteImage(current.object_id);
    setIdx((i) => Math.max(0, Math.min(i, instances.length - 2)));
  };

  const act = (fn) => { const el = document.querySelectorAll("[data-testid='dv-cell'] [data-testid='dv-canvas']")[layout === 4 ? focus : 0]; if (el && el._apply) el._apply(fn); };
  const reset = () => { const el = document.querySelectorAll("[data-testid='dv-cell'] [data-testid='dv-canvas']")[layout === 4 ? focus : 0]; try { if (el) cornerstone.reset(el); } catch { /* noop */ } };
  const fit = () => { const el = document.querySelectorAll("[data-testid='dv-cell'] [data-testid='dv-canvas']")[layout === 4 ? focus : 0]; try { if (el) cornerstone.fitToWindow(el); } catch { /* noop */ } };
  const TBtn = ({ icon: Icon, label, active, onClick, testid, accent }) => (
    <button onClick={onClick} data-testid={testid} title={label}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors ${
        active ? "bg-sky-600 text-white" : accent === "primary" ? "bg-sky-600/90 text-white hover:bg-sky-500"
          : accent === "danger" ? "bg-rose-600 text-white hover:bg-rose-500" : "bg-slate-800 text-slate-200 hover:bg-slate-700"}`}>
      <Icon size={15} /><span className="hidden lg:inline">{label}</span>
    </button>
  );

  return (<div className="fixed inset-0 bg-black/80 z-[70] flex flex-col" data-testid="dicom-viewer">
    <div className="flex items-center justify-between gap-2 p-3 text-white bg-slate-900 flex-wrap">
      <div className="text-sm font-medium">{t("diag.vw_clinical")} · {idx + 1}/{instances.length}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <TBtn icon={Move} label={t("diag.vw_pan")} testid="dv-pan" active={mode === "pan" && !measure} onClick={() => { setMode("pan"); setMeasure(false); }} />
        <TBtn icon={Contrast} label={t("diag.vw_wl")} testid="dv-wl" active={mode === "wl" && !measure} onClick={() => { setMode("wl"); setMeasure(false); }} />
        <TBtn icon={Sparkles} label={t("diag.vw_invert")} testid="dv-invert" onClick={() => act((v) => { v.invert = !v.invert; })} />
        <TBtn icon={ZoomIn} label={`${t("diag.vw_zoom")}+`} testid="dv-zoomin" onClick={() => act((v) => { v.scale *= 1.2; })} />
        <TBtn icon={ZoomOut} label={`${t("diag.vw_zoom")}−`} testid="dv-zoomout" onClick={() => act((v) => { v.scale = Math.max(0.1, v.scale / 1.2); })} />
        <TBtn icon={Maximize2} label={t("diag.vw_fit")} testid="dv-fit" onClick={fit} />
        <TBtn icon={RotateCw} label={t("diag.vw_rotate")} testid="dv-rotate" onClick={() => act((v) => { v.rotation = (v.rotation || 0) + 90; })} />
        {layout === 1 && <TBtn icon={Ruler} label={t("diag.vw_measure")} testid="dv-measure" active={measure} onClick={() => setMeasure((m) => !m)} />}
        <TBtn icon={LayoutGrid} label={`${t("diag.vw_layout")} ${layout === 1 ? "1×1" : "2×2"}`} testid="dv-layout" onClick={() => setLayout(layout === 1 ? 4 : 1)} />
        <TBtn icon={RefreshCw} label={t("diag.vw_reset")} testid="dv-reset" onClick={reset} />
        {folderId && <TBtn icon={FileText} label={t("diag.vw_redact")} testid="dv-redact" accent="primary" onClick={() => setEditor(true)} />}
        {onDeleteImage && current?.canDelete && <TBtn icon={Trash2} label={t("diag.vw_delete")} testid="dv-delete" accent="danger" onClick={handleDelete} />}
        <TBtn icon={X} label={t("diag.close")} testid="dv-close" accent="danger" onClick={onClose} />
      </div>
    </div>
    <div className="flex-1 flex min-h-0">
      {instances.length > 1 && <div className="w-24 overflow-y-auto bg-slate-950 p-2 space-y-1">
        {instances.map((ins, i) => (
          <button key={ins.object_id} onClick={() => setIdx(i)} className={`w-full text-[10px] rounded px-1 py-2 ${i === idx ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`} data-testid="dv-thumb">#{i + 1}</button>))}
      </div>}
      <div className="flex-1 relative">
        <div className={`absolute inset-0 grid ${layout === 4 ? "grid-cols-2 grid-rows-2 gap-0.5" : "grid-cols-1"}`}>
          {cells.map((inst, k) => (
            <Cell key={`${inst.object_id}-${layout}`} ingestId={ingestId} instance={inst}
              focused={layout === 1 || focus === k} onFocus={() => setFocus(k)} mode={mode}
              measure={measure} setMeasureInfo={layout === 1 ? setMeasureInfo : undefined} />))}
        </div>
        {measureInfo && <div className="absolute bottom-3 left-3 z-10 bg-slate-900/80 text-slate-100 text-xs px-2.5 py-1 rounded-md" data-testid="dv-measure-info">{t("diag.vw_measure")}: {measureInfo}</div>}
      </div>
    </div>
    {editor && folderId && <ClinicalDocEditor ctx={{ folderId }} onClose={() => setEditor(false)} onSaved={onSaved} />}
  </div>);
}
